import api from "@/lib/axiosConfig";
import { Multiplesample } from "@/lib/types";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface MultipleSampleDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

const getAllMultipleSamples = async ({
  where,
  order,
  take,
  skip,
}: MultipleSampleDTO): Promise<Multiplesample[]> => {
  const { data } = await api.post<Multiplesample[]>(
    `back/api/multiplesample?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
  );
  return data;
};

export const useGetAllMultipleSamples = ({
  where,
  order,
  take,
  skip,
}: MultipleSampleDTO) => {
  return useQuery({
    queryKey: ["multiplesamples", where, order, take, skip],
    queryFn: () => getAllMultipleSamples({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

export type CreateMultipleSampleParams = {
  exemplaireDansEstampesMultiplesDescriptionNumero: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroInventaire?: string;
  exemplaireDansEstampesMultiplesDescriptionLocalisation?: string;
  exemplaireDansEstampesMultiplesDescriptionNotes?: string;
  multipleprints?: {connect: {id: string}}
};

async function createMultipleSample(multiplesample: CreateMultipleSampleParams): Promise<Multiplesample> {
  const { data } = await api.post<Multiplesample>(`back/api/multiplesample/create`, multiplesample);
  return data;
}

export const useCreateMultipleSample = () => {
  const queryClient = useQueryClient();
  return useMutation<Multiplesample, Error, CreateMultipleSampleParams>({
    mutationFn: createMultipleSample,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["multiplesamples"] });
      queryClient.invalidateQueries({ queryKey: ["multiplesample", data.id] });
    },
  });
};

export type UpdateMultipleSampleParams = {
  data: CreateMultipleSampleParams;
  where: any;
};

async function updateMultipleSample(params: UpdateMultipleSampleParams) {
  const { data } = await api.post(`back/api/multiplesample/update`, {
    ...params.data,
    where: params.where,
  });
  return data;
}

export const useUpdateMultipleSample = () => {
  const queryClient = useQueryClient();
  return useMutation<Multiplesample, Error, UpdateMultipleSampleParams>({
    mutationFn: updateMultipleSample,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["multiplesamples"] });
      queryClient.invalidateQueries({ queryKey: ["multiplesample", data.id] });
    },
  });
};

async function deleteMultipleSample(id: string) {
  const { data } = await api.post(`back/api/multiplesample/delete`, {
    where: { id },
  });
  return data;
}

export const useDeleteMultipleSample = () => {
  const queryClient = useQueryClient();
  return useMutation({ 
    mutationFn: deleteMultipleSample,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["multiplesamples"] });
    },
  });
};

async function getMultipleSampleById(id: string) {
  const { data } = await api.get(`back/api/multiplesample/${id}`);
  return data;
}

export const useGetMultipleSampleById = (id: string) => {
  return useQuery({
    queryKey: ["multiplesample", id],
    queryFn: () => getMultipleSampleById(id),
  });
};